import { Button, DatePicker, Typography } from "antd";
import { Dayjs } from "dayjs";
import { ReactNode, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getCompanies,
  getCompanyDepartments,
} from "../../repository/data.repository";
import {
  createScopedSearchRequest,
  createSearchRequest,
} from "../../hooks/useSearchRequest";
import {
  QueryCondition,
  QueryOperation,
} from "../../code/search-query/search-query";
import { resolveRange } from "./AnalyticsRange";
import DashboardMetricCard, {
  DashboardMetricCardProps,
} from "./DashboardMetricCard";
import RemoteSelect from "./RemoteSelect";

// The section's resolved filter state, handed to custom cards so they stay driven by the same
// filter bar as the descriptor charts.
export interface DashboardSectionScope {
  readonly companyUid?: string;
  readonly departmentUid?: string;
  /** Resolved [from, to] datetime bounds when a date range is picked. */
  readonly range?: [string, string];
}

interface DashboardChartBase {
  readonly title: string;
  // Whether the card's data source supports a department filter (the section only applies
  // `departmentUid` to cards that can be filtered by it).
  readonly supportsDepartment?: boolean;
  // Admin route this card drills through to, so a number on the dashboard can be opened as the
  // list behind it. Omitted where the platform has no module to land on — better no link than one
  // that goes somewhere unrelated.
  readonly drillTo?: string;
  // Link wording; defaults to "View".
  readonly drillLabel?: string;
}

// Descriptor chart: a paginated fetch reduced client-side by DashboardMetricCard.
export interface DashboardDescriptorChart
  extends DashboardChartBase,
    Pick<DashboardMetricCardProps, "fetch" | "mode" | "windowDays"> {}

// Custom card: the caller renders its own card off the section's resolved scope — used for
// metrics backed by server-side aggregate endpoints rather than a paginated list.
export interface DashboardCustomChart extends DashboardChartBase {
  readonly render: (scope: DashboardSectionScope) => ReactNode;
  /** Span the whole section row (for multi-chart strips) instead of sharing it as one card. */
  readonly fullWidth?: boolean;
}

// One card in a section — either a declarative metric descriptor or a custom-rendered card.
export type DashboardChart = DashboardDescriptorChart | DashboardCustomChart;

// The drill-down from a dashboard number to the list behind it. Renders nothing when a card has no
// destination, so a card without a module is simply link-less rather than pointing somewhere wrong.
// Navigation goes through useNavigate to match how the rest of the app moves between routes.
export function DrillLink(props: { readonly to?: string; readonly label?: string }) {
  const navigate = useNavigate();
  if (!props.to) return null;
  // Absolute, always. A relative path resolves against the CURRENT route, and these render inside
  // DashboardPage (route "dashboard"), so "incidents/all" became "/dashboard/incidents/all",
  // matched nothing, and the catch-all bounced the user back to where they started — a link that
  // looked live and did nothing. NavigationMenu gets away with relative paths only because it sits
  // in MasterPage, at the "/*" level.
  const to = props.to.startsWith("/") ? props.to : `/${props.to}`;
  return (
    <div className="mb-1 flex justify-end">
      <Button type="link" size="small" className="px-0" onClick={() => navigate(to)}>
        {props.label ?? "View"} →
      </Button>
    </div>
  );
}

// A section's filter bar governs every card under it, but `departmentUid` is only applied to the cards
// whose data source can honour it — the others silently keep answering the company-wide question. An
// operator reading "IMEQ / Administrative" above a company-wide number has no way to tell the
// department was dropped, and the figure looks like an answer to the question they asked. Saying so is
// the cheap half of the fix; the other half is the endpoint gaining department support, which is a
// larger change and a product decision.
function DepartmentNotApplied(props: { readonly show: boolean }) {
  if (!props.show) return null;
  return (
    <Typography.Text type="secondary" className="mb-1 block text-xs">
      Company-wide — not broken down by department.
    </Typography.Text>
  );
}

export interface DashboardSectionProps {
  readonly charts: DashboardChart[];
}

const eq = (field: string, value: string): QueryCondition => ({
  field,
  operation: QueryOperation.Equal,
  values: [value],
});

const companySearch = createSearchRequest(getCompanies, "name");

// A module's charts plus their OWN filter row (company · department · date range), scoping only this
// section. Company + date apply to every chart; department is offered only when a chart supports it and
// is applied per-chart (so unsupported endpoints never receive an invalid `departmentUid` filter).
function DashboardSection(props: DashboardSectionProps) {
  const [companyUid, setCompanyUid] = useState<string | undefined>();
  const [departmentUid, setDepartmentUid] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  const showDepartment = props.charts.some((c) => c.supportsDepartment);

  // Departments cascade off the chosen company: the scoped request reads the company live (via a
  // ref, so the loader keeps a stable identity) and RemoteSelect refetches on open.
  const companyUidRef = useRef<string | undefined>(undefined);
  companyUidRef.current = companyUid;
  const departmentSearch = useMemo(
    () =>
      createScopedSearchRequest(
        getCompanyDepartments,
        "name",
        "companyUid",
        () => companyUidRef.current,
      ),
    [],
  );

  // Resolved date bounds (start-of-day / end-of-day) for the cards, or undefined when no range is picked.
  const resolvedRange: [string, string] | undefined =
    range && range[0] && range[1]
      ? (() => {
          const r = resolveRange(
            {
              mode: "custom",
              custom: {
                start: range[0].format("YYYY-MM-DD"),
                end: range[1].format("YYYY-MM-DD"),
              },
            },
            [],
          );
          return r.start && r.end ? [r.start, r.end] : undefined;
        })()
      : undefined;

  const companyScope: QueryCondition[] = companyUid
    ? [eq("companyUid", companyUid)]
    : [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Typography.Text type="secondary" className="text-xs">
            Company
          </Typography.Text>
          <RemoteSelect
            className="w-56"
            placeholder="All companies"
            value={companyUid}
            onChange={(v) => {
              setCompanyUid(v);
              // Cascade: a department belongs to one company — clear it when the company changes.
              setDepartmentUid(undefined);
            }}
            request={companySearch}
          />
        </div>
        {showDepartment && (
          <div className="flex flex-col gap-1">
            <Typography.Text type="secondary" className="text-xs">
              Department
            </Typography.Text>
            <RemoteSelect
              className="w-52"
              placeholder="All departments"
              disabled={!companyUid}
              value={departmentUid}
              onChange={setDepartmentUid}
              request={departmentSearch}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Typography.Text type="secondary" className="text-xs">
            Date range
          </Typography.Text>
          <DatePicker.RangePicker
            value={range}
            onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
            allowClear
          />
        </div>
      </div>

      <div className="flex flex-row flex-wrap gap-3">
        {props.charts.map((c) => {
          if ("render" in c) {
            return (
              <div
                key={c.title}
                className={c.fullWidth ? "basis-full" : "min-w-[340px] flex-1"}
              >
                <DrillLink to={c.drillTo} label={c.drillLabel} />
                <DepartmentNotApplied show={!!departmentUid && !c.supportsDepartment} />
                {c.render({
                  companyUid,
                  departmentUid: c.supportsDepartment ? departmentUid : undefined,
                  range: resolvedRange,
                })}
              </div>
            );
          }
          const scope: QueryCondition[] = [
            ...companyScope,
            ...(departmentUid && c.supportsDepartment
              ? [eq("departmentUid", departmentUid)]
              : []),
          ];
          return (
            <div key={c.title} className="min-w-[340px] flex-1">
              <DrillLink to={c.drillTo} label={c.drillLabel} />
              <DepartmentNotApplied show={!!departmentUid && !c.supportsDepartment} />
              <DashboardMetricCard
                title={c.title}
                fetch={c.fetch}
                mode={c.mode}
                windowDays={c.windowDays}
                scope={scope}
                range={resolvedRange}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default DashboardSection;
